import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ClipboardCheck, Play, RefreshCw, ArrowLeft } from "lucide-react";
import { theme, card, btnPrimary, countTag, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { LoadingBlock } from "../components/Spinner";
import { SheetCard, DimensionHeading } from "../components/Drawing";
import { fmtDate, fmtDateTime } from "../lib/format";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";
import { EmptyState, Field, input, secondaryBtn, tableHeadRow } from "./resilience/shared";

// Mock audit rehearsal (backend/src/routes/mockAudit.routes.ts, lib/mockAudit.ts): the platform plays the
// certification bureau for one enrolled certifiable standard. A seeded, reproducible sample, the evidence trail
// and record checks behind each sampled requirement, the evidence an auditor would ask for and the likely
// findings, every cited record checked to exist. Private to the organisation and labelled a rehearsal
// everywhere: no bureau sees it, nothing is an auditor finding, no SOA status changes. A likely finding
// becomes a corrective action only when a member raises it.

type Grade = "major" | "minor" | "observation" | "opportunity";
type Counts = Record<Grade, number>;
interface Citation { key: string; kind: string; id: string; label: string; href: string | null; controlId: string | null }
interface Finding { key: string; controlId: string; frameworkId: string; grade: Grade; downgradedFrom: Grade | null; title: string; gap: string; citations: Citation[] }
interface EvidenceRequest { controlId: string; request: string; reason: string }
interface SampleDraw { population: string; populationCount: number; populationDigest: string; items: Array<{ id: string; label: string; date: string | null }> }
interface ControlContext { controlId: string; name: string; key: string; documents: Array<{ key: string; fileName: string }>; recordChecks: Array<{ title: string; status: string }> }
interface Rehearsal {
  id: string; reference: string; frameworkId: string; frameworkLabel: string; status: "queued" | "running" | "done" | "failed";
  controlSampleSize: number; recordSampleSize: number; periodStart: string; periodEnd: string; createdAt: string; completedAt: string | null;
  failureMessage: string | null; requestedBy: string | null; counts: Counts | null;
}
interface RehearsalDetail extends Rehearsal {
  seed: string; summary: string | null; findings: Finding[] | null; evidenceRequests: EvidenceRequest[] | null;
  sample: { controls: { populationCount: number; ids: string[] }; registers: SampleDraw[] } | null;
  context: { controls: ControlContext[] } | null;
  citationReport: { citationsDropped: number; findingsDowngraded: number; findingsDropped: Array<{ title: string; reason: string }>; requestsDropped: number; malformed: number } | null;
}
interface ListView {
  label: string; notice: string; gradeLabels: Record<Grade, string>;
  sampleSizes: { controls: { min: number; max: number; default: number }; records: { min: number; max: number; default: number } };
  standards: Array<{ id: string; label: string }>; rehearsals: Rehearsal[];
}
interface DetailView { rehearsal: RehearsalDetail; actions: Record<string, { id: string; displayId: number | null; status: string }>; notice: string; gradeLabels: Record<Grade, string> }
interface RedrawView { seed: string; check: { reproducible: boolean; unchanged: boolean; controls: { populationCountNow: number }; registers: Array<{ population: string; populationUnchanged: boolean; populationCountNow: number }> } }

const POPULATION_LABELS: Record<string, string> = {
  leavers: "Leavers", incidents: "Security incidents", risks: "Risks", corrective_actions: "Corrective actions",
  supplier_assessments: "Supplier assessments", training_records: "Training records",
};
const GRADE_TONE: Record<Grade, { color: string; bg: string }> = {
  major: { color: theme.color.dangerText, bg: theme.color.dangerBg },
  minor: { color: theme.color.warningText, bg: theme.color.warningBg },
  observation: { color: theme.color.muted, bg: theme.color.panelAlt },
  opportunity: { color: theme.color.successText, bg: theme.color.successBg },
};
const STATUS_WORDS: Record<Rehearsal["status"], string> = { queued: "Waiting to start", running: "Running", done: "Finished", failed: "Stopped" };
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, textAlign: "left" };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText };
const actionRef = (n: number | null) => (n != null ? `CA-${String(n).padStart(3, "0")}` : "CA");

function RehearsalTag() {
  return <span style={{ ...countTag, background: theme.color.accentBg, color: theme.color.accentText }}>Rehearsal</span>;
}

function Notice({ text: words }: { text: string }) {
  return (
    <div style={{ ...card, padding: "10px 14px", borderLeft: `3px solid ${theme.color.accent}`, display: "flex", gap: 10, alignItems: "center" }}>
      <RehearsalTag />
      <span style={text.body}>{words}</span>
    </div>
  );
}

function GradeChip({ grade, labels }: { grade: Grade; labels: Record<Grade, string> }) {
  const tone = GRADE_TONE[grade];
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: tone.bg, color: tone.color, whiteSpace: "nowrap" }}>{labels[grade]}</span>;
}

function CountsLine({ counts }: { counts: Counts | null }) {
  if (!counts) return <span>-</span>;
  const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  return <span>{counts.major} major, {counts.minor} minor, {n(counts.observation, "observation", "observations")}, {n(counts.opportunity, "opportunity", "opportunities")}</span>;
}

export function MockAuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("rehearsal");
  const [list, setList] = useState<ListView | null>(null);
  const [error, setError] = useState("");
  const [frameworkId, setFrameworkId] = useState("");
  const [controlSize, setControlSize] = useState<number | "">("");
  const [recordSize, setRecordSize] = useState<number | "">("");
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const v = await api.get<ListView>("/mock-audits");
      setList(v);
      setError("");
      setFrameworkId((f) => f || v.standards[0]?.id || "");
    } catch (e) { setError(apiErrorMessage(e, "Could not load the mock audit rehearsals.")); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const open = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("rehearsal", id); else next.delete("rehearsal");
    setSearchParams(next, { replace: true });
  };

  const start = async () => {
    setStarting(true);
    try {
      const res = await api.post<{ rehearsalId: string }>("/mock-audits", {
        frameworkId,
        ...(controlSize !== "" ? { controlSampleSize: controlSize } : {}),
        ...(recordSize !== "" ? { recordSampleSize: recordSize } : {}),
      });
      await load();
      open(res.rehearsalId);
    } catch (e) {
      // Reload first: a refused start may have closed its row, and the reload clears the error line.
      await load();
      setError(apiErrorMessage(e, "The rehearsal could not be started."));
    }
    finally { setStarting(false); }
  };

  if (openId) return <RehearsalView id={openId} onBack={() => { open(null); void load(); }} />;

  const running = list?.rehearsals.some((r) => r.status === "queued" || r.status === "running");
  return (
    <div className="page-pad">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><ClipboardCheck size={20} /> Mock audit</h1>
        <p style={{ ...pageSubtitle, maxWidth: 780 }}>
          Rehearse a certification audit before the real one. The platform draws a sample the way an auditor does, reads the evidence
          trail and register checks behind each sampled requirement, and acts as the lead auditor: what it would ask to see, and what it
          would probably raise. Every record a likely finding cites is checked to exist in your organisation.
        </p>
      </div>
      {list && <div style={{ marginBottom: 16 }}><Notice text={list.notice} /></div>}
      {error && <div style={{ ...errorText, marginBottom: 12 }}>{error}</div>}
      {!list ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <SheetCard label="Run a rehearsal">
            {list.standards.length === 0 ? (
              <p style={{ ...text.body, margin: 0 }}>There is no enrolled certifiable standard your role covers. A rehearsal is for a standard a certification body audits.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, alignItems: "end" }}>
                <Field label="Standard">
                  <select style={input} value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)}>
                    {list.standards.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Requirements to sample" help={`${list.sampleSizes.controls.min} to ${list.sampleSizes.controls.max}; ${list.sampleSizes.controls.default} if left empty`}>
                  <input style={input} type="number" min={list.sampleSizes.controls.min} max={list.sampleSizes.controls.max} value={controlSize} placeholder={String(list.sampleSizes.controls.default)} onChange={(e) => setControlSize(e.target.value === "" ? "" : Number(e.target.value))} />
                </Field>
                <Field label="Records per register" help={`${list.sampleSizes.records.min} to ${list.sampleSizes.records.max}, from the last 12 months`}>
                  <input style={input} type="number" min={list.sampleSizes.records.min} max={list.sampleSizes.records.max} value={recordSize} placeholder={String(list.sampleSizes.records.default)} onChange={(e) => setRecordSize(e.target.value === "" ? "" : Number(e.target.value))} />
                </Field>
                <div>
                  <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} disabled={starting || !!running || !frameworkId} onClick={() => void start()}>
                    <Play size={14} /> {starting ? "Starting…" : "Run a rehearsal"}
                  </button>
                  {running && <div style={{ ...text.hint, marginTop: 6 }}>A rehearsal is already running.</div>}
                </div>
              </div>
            )}
          </SheetCard>

          <SheetCard label="Rehearsals" padding={0}>
            {list.rehearsals.length === 0 ? (
              <EmptyState title="No rehearsals yet" detail="Run one to see what an auditor would probably ask for and raise. It stays private to your organisation." />
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={tableHeadRow}><th style={th}>Rehearsal</th><th style={th}>Standard</th><th style={th}>Run</th><th style={th}>Status</th><th style={th}>Likely findings</th></tr></thead>
                <tbody>
                  {list.rehearsals.map((r) => (
                    <tr key={r.id}>
                      <td style={td}><button type="button" style={{ background: "none", border: "none", padding: 0, color: theme.color.ref, cursor: "pointer", ...text.ref }} onClick={() => open(r.id)}>{r.reference}</button></td>
                      <td style={td}>{r.frameworkLabel}</td>
                      <td style={td}>{fmtDateTime(r.createdAt)}{r.requestedBy ? ` by ${r.requestedBy}` : ""}</td>
                      <td style={td}>{STATUS_WORDS[r.status]}</td>
                      <td style={td}><CountsLine counts={r.counts} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SheetCard>
        </div>
      )}
    </div>
  );
}

function RehearsalView({ id, onBack }: { id: string; onBack: () => void }) {
  const [data, setData] = useState<DetailView | null>(null);
  const [error, setError] = useState("");
  const [redraw, setRedraw] = useState<RedrawView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get<DetailView>(`/mock-audits/${id}`)); setError(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the rehearsal.")); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  // Poll while the worker runs it.
  const status = data?.rehearsal.status;
  useEffect(() => {
    if (status !== "queued" && status !== "running") return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [status, load]);

  const raise = async (key: string) => {
    setBusy(key);
    try { await api.post(`/mock-audits/${id}/findings/${key}/action`, {}); await load(); }
    catch (e) { setError(apiErrorMessage(e, "The corrective action could not be raised.")); }
    finally { setBusy(null); }
  };
  const checkDraw = async () => {
    setBusy("redraw");
    try { setRedraw(await api.get<RedrawView>(`/mock-audits/${id}/redraw`)); }
    catch (e) { setError(apiErrorMessage(e, "The sample could not be drawn again.")); }
    finally { setBusy(null); }
  };

  const r = data?.rehearsal;
  const names = new Map((r?.context?.controls ?? []).map((c) => [c.controlId, c.name]));
  const controlTitle = (cid: string) => `${displayControlRef(cid)} ${displayControlTitle(cid, names.get(cid) ?? "")}`.trim();
  const requestsBy = new Map<string, EvidenceRequest[]>();
  for (const q of r?.evidenceRequests ?? []) requestsBy.set(q.controlId, [...(requestsBy.get(q.controlId) ?? []), q]);

  return (
    <div className="page-pad">
      <button type="button" style={{ ...secondaryBtn, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14 }} onClick={onBack}><ArrowLeft size={14} /> All rehearsals</button>
      {error && <div style={{ ...errorText, marginBottom: 12 }}>{error}</div>}
      {!data || !r ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <div>
            <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={text.ref}>{r.reference}</span> Mock audit of {r.frameworkLabel} <RehearsalTag />
            </h1>
            <p style={{ ...pageSubtitle, maxWidth: 780 }}>
              Run {fmtDateTime(r.createdAt)}{r.requestedBy ? ` by ${r.requestedBy}` : ""}. Records sampled from {fmtDate(r.periodStart)} to {fmtDate(r.periodEnd)}.
            </p>
          </div>
          <Notice text={data.notice} />

          {(r.status === "queued" || r.status === "running") && (
            <SheetCard label="In progress"><p style={{ ...text.body, margin: 0 }}>The rehearsal is {r.status === "queued" ? "waiting to start" : "running"}. This page updates by itself.</p></SheetCard>
          )}
          {r.status === "failed" && (
            <SheetCard label="Stopped"><p style={{ ...text.body, margin: 0, color: theme.color.dangerText }}>{r.failureMessage ?? "The rehearsal stopped."}</p></SheetCard>
          )}

          {r.status === "done" && (
            <>
              <SheetCard label="What the rehearsal found" featured>
                {r.summary && <p style={{ ...text.body, marginTop: 0 }}>{r.summary}</p>}
                <div style={{ ...text.hint }}><CountsLine counts={r.counts} />. Likely findings are the platform's reading of your records, not an auditor's decision.</div>
              </SheetCard>

              <section>
                <DimensionHeading count={r.findings?.length ?? 0}>Likely findings</DimensionHeading>
                {(r.findings ?? []).length === 0 ? (
                  <div style={{ ...card, padding: 16 }}><p style={{ ...text.body, margin: 0 }}>Nothing the sampled evidence supports raising.</p></div>
                ) : (
                  <div style={{ display: "grid", gap: 12 }}>
                    {(r.findings ?? []).map((f) => {
                      const action = data.actions[f.key];
                      const live = action && action.status !== "verified" && action.status !== "cancelled";
                      return (
                        <div key={f.key} style={{ ...card, padding: 16 }}>
                          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                            <GradeChip grade={f.grade} labels={data.gradeLabels} />
                            <span style={text.itemTitle}>{f.title}</span>
                          </div>
                          <div style={{ ...text.hint, marginBottom: 8 }}>{controlTitle(f.controlId)}</div>
                          <p style={{ ...text.body, margin: "0 0 8px" }}>{f.gap}</p>
                          {f.downgradedFrom && <div style={{ ...text.hint, marginBottom: 8 }}>Lowered from {data.gradeLabels[f.downgradedFrom].toLowerCase()}: a record it cited could not be found.</div>}
                          <div style={{ ...text.hint }}>
                            Rests on: {f.citations.map((c, i) => (
                              <span key={c.key}>{i > 0 ? "; " : ""}{c.href ? <Link to={c.href} style={{ color: theme.color.ref }}>{c.label}</Link> : c.label}</span>
                            ))}
                          </div>
                          <div style={{ marginTop: 10 }}>
                            {live ? (
                              <Link to={`/improvement?action=${action.id}`} style={{ ...text.hint, color: theme.color.ref }}>{actionRef(action.displayId)} raised from this finding</Link>
                            ) : (
                              <button type="button" style={secondaryBtn} disabled={busy === f.key} onClick={() => void raise(f.key)}>{busy === f.key ? "Raising…" : "Raise a corrective action"}</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <SheetCard label="Evidence an auditor would ask for">
                {requestsBy.size === 0 ? <p style={{ ...text.body, margin: 0 }}>No requests were returned.</p> : (
                  <div style={{ display: "grid", gap: 12 }}>
                    {[...requestsBy.entries()].map(([cid, qs]) => (
                      <div key={cid}>
                        <div style={text.itemTitle}>{controlTitle(cid)}</div>
                        <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                          {qs.map((q, i) => <li key={i} style={text.body}>{q.request}{q.reason ? <span style={text.hint}> ({q.reason})</span> : null}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </SheetCard>

              {r.citationReport && (r.citationReport.citationsDropped > 0 || r.citationReport.findingsDropped.length > 0 || r.citationReport.requestsDropped > 0) && (
                <SheetCard label="Left out because the records did not support it">
                  <p style={{ ...text.body, marginTop: 0 }}>
                    {r.citationReport.citationsDropped} citation{r.citationReport.citationsDropped === 1 ? "" : "s"} named no record in your organisation and {r.citationReport.citationsDropped === 1 ? "was" : "were"} removed;
                    {" "}{r.citationReport.findingsDowngraded} finding{r.citationReport.findingsDowngraded === 1 ? " was" : "s were"} lowered a grade.
                  </p>
                  {r.citationReport.findingsDropped.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {r.citationReport.findingsDropped.map((d, i) => <li key={i} style={text.hint}>{d.title}: {d.reason}</li>)}
                    </ul>
                  )}
                </SheetCard>
              )}
            </>
          )}

          {r.sample && (
            <SheetCard label="The sample" actions={
              <button type="button" style={{ ...secondaryBtn, display: "inline-flex", alignItems: "center", gap: 6 }} disabled={busy === "redraw"} onClick={() => void checkDraw()}>
                <RefreshCw size={13} /> Draw it again
              </button>
            }>
              <p style={{ ...text.hint, marginTop: 0 }}>
                Drawn from seed <span style={text.mono}>{r.seed}</span>: {r.sample.controls.ids.length} of {r.sample.controls.populationCount} applicable requirements, and up to {r.recordSampleSize} records from each register.
              </p>
              {redraw && (
                <p style={{ ...text.body, color: redraw.check.reproducible && redraw.check.unchanged ? theme.color.successText : theme.color.warningText }}>
                  {redraw.check.reproducible && redraw.check.unchanged
                    ? "Drawn again from today's records, the seed gives the same sample."
                    : `The records have changed since: ${redraw.check.registers.filter((x) => !x.populationUnchanged).map((x) => POPULATION_LABELS[x.population] ?? x.population).join(", ") || "the requirements"} ${redraw.check.reproducible ? "changed, but the same items are still drawn" : "no longer give the same items"}.`}
                </p>
              )}
              <div style={{ ...text.label }}>Requirements</div>
              <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
                {r.sample.controls.ids.map((cid) => <li key={cid} style={text.body}>{controlTitle(cid)}</li>)}
              </ul>
              {r.sample.registers.filter((g) => g.items.length > 0).map((g) => (
                <div key={g.population} style={{ marginBottom: 10 }}>
                  <div style={{ ...text.label }}>{POPULATION_LABELS[g.population] ?? g.population} ({g.items.length} of {g.populationCount})</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {g.items.map((it) => <li key={it.id} style={text.body}>{it.label}{it.date ? <span style={text.hint}> · {fmtDate(it.date)}</span> : null}</li>)}
                  </ul>
                </div>
              ))}
            </SheetCard>
          )}
        </div>
      )}
    </div>
  );
}
